import { useEffect } from 'react';
import { ClientGameGate } from '@low6-pickem/ui';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { UserProvider } from '@/contexts/UserContext';
import { CompetitionProvider } from '@/contexts/CompetitionContext';
import { TenantProvider } from '@/contexts/TenantContext';
import { QueryProvider } from '@/providers/QueryProvider';
import { DebugFooter } from '@/components/debug/DebugFooter';
import { OAuthCallback } from '@/components/auth/OAuthCallback';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { initializeGPT, enableAndDisplayAds } from '@/utils/gpt';
import { useGetTenant } from '@/hooks/useTenant';
import { getGAMConfigFromTenant } from '@/config/ads.config';
import Index from './pages/Index';
import NotFound from './pages/NotFound';
import EnvVars from './pages/EnvVars';

import {
  resolveTenantFromUrl,
  isUrlTenantMode,
  getTenantBasePath,
  isClientGameEnabled,
  GameEntry,
} from '@low6-pickem/frontend-shared';

const TENANT_SLUG = resolveTenantFromUrl();
const TENANT_BASE_PATH = getTenantBasePath();

const AppContent = () => {
  const { isLoading, authError } = useAuth();
  const {
    data: tenant,
    isLoading: isTenantLoading,
    error: tenantError,
  } = useGetTenant(TENANT_SLUG);

  // Initialize Google Publisher Tags (GPT) on app mount
  useEffect(() => {
    // Don't initialize ads until tenant config is loaded
    if (isTenantLoading || !tenant) {
      return;
    }

    const gamConfig = getGAMConfigFromTenant(tenant);

    // Only initialize GPT if tenant has GAM enabled
    if (!gamConfig || !gamConfig.enabled) {
      return;
    }

    const initAds = async () => {
      try {
        await initializeGPT();

        enableAndDisplayAds();
      } catch (error) {
        console.error('[App] Failed to initialize GPT:', error);
      }
    };

    initAds();
  }, [tenant, isTenantLoading]);

  // Allow OAuth callback to process regardless of loading state
  const isOAuthCallback = window.location.pathname.endsWith('/auth/callback');

  if (isOAuthCallback) {
    return (
      <TooltipProvider>
        <BrowserRouter basename={TENANT_BASE_PATH || undefined}>
          <Routes>
            <Route path="/auth/callback" element={<OAuthCallback />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    );
  }

  // Show error message if tenant loading failed
  if (tenantError) {
    // If we're in URL tenant mode and tenant is invalid, redirect to default
    if (isUrlTenantMode()) {
      window.location.href = '/';
      return null;
    }
    return (
      <div className="flex items-center justify-center min-h-screen bg-app-background">
        <div className="text-center max-w-md px-6">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-white text-xl font-bold mb-2">Configuration Error</h2>
          <p className="text-gray-400 mb-4">Failed to load application configuration</p>
          <p className="text-gray-500 text-sm">
            Please check your connection and try again. If the problem persists, contact support.
          </p>
        </div>
      </div>
    );
  }

  // Show error message if authentication failed
  if (authError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-app-background">
        <div className="text-center max-w-md px-6">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-white text-xl font-bold mb-2">Authentication Failed</h2>
          <p className="text-gray-400 mb-4">{authError.message}</p>
          <p className="text-gray-500 text-sm">
            Please check your connection and try again. If the problem persists, contact support.
          </p>
        </div>
      </div>
    );
  }

  // Show loading spinner while authenticating or loading tenant
  if (isLoading || isTenantLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-app-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pickem-primary mx-auto mb-4"></div>
          <p className="text-white">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <TenantProvider tenant={tenant}>
      <UserProvider>
        <CompetitionProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter basename={TENANT_BASE_PATH || undefined}>
              <Routes>
                {/* DK iframe entry (DKPLAT-467): /pickem?gameId=&tenantId= and
                    proxy-stripped /?gameId= both deep-link to /game/:gameId */}
                <Route
                  path="/"
                  element={
                    <GameEntry>
                      <Index />
                    </GameEntry>
                  }
                />
                <Route
                  path="/pickem"
                  element={
                    <GameEntry>
                      <Index />
                    </GameEntry>
                  }
                />
                <Route path="/welcome" element={<Index />} />
                <Route path="/how-to-play" element={<Index />} />
                <Route path="/main" element={<Index />} />
                <Route path="/main/:tab" element={<Index />} />
                <Route path="/game/:gameId" element={<Index />} />
                <Route path="/league/:leagueName" element={<Index />} />
                <Route path="/env-vars" element={<EnvVars />} />
                <Route path="/auth/callback" element={<OAuthCallback />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
            {import.meta.env.VITE_SHOW_DEBUG_FOOTER === '1' && <DebugFooter />}
          </TooltipProvider>
        </CompetitionProvider>
      </UserProvider>
    </TenantProvider>
  );
};

const App = () => {
  // Layer A: don't register this game's routes if the client didn't deploy it.
  return (
    <ClientGameGate enabled={isClientGameEnabled('pickem')}>
      <ErrorBoundary onError={error => console.error('[App Error]', error)}>
        <QueryProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </QueryProvider>
      </ErrorBoundary>
    </ClientGameGate>
  );
};

export default App;
